import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';

function EditEventModal({ isOpen, onClose, event, onSuccess }) {
    const { t } = useTranslation();
    const { getAuthToken, isAdmin } = useAuth();
    const [loading, setLoading] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({
        name_en: '',
        name_tl: '',
        description_en: '',
        description_tl: '',
        start_time: '',
        place_name: '',
        place_address: '',
        mapsUrl: '',
        maxSlots: 5
    });

    useEffect(() => {
        if (event && isOpen) {
            // Extract bilingual data
            const nameEn = typeof event.name === 'object' ? event.name.en : event.name || '';
            const nameTl = typeof event.name === 'object' ? event.name.tl : '';
            const descEn = typeof event.description === 'object' ? event.description.en : event.description || '';
            const descTl = typeof event.description === 'object' ? event.description.tl : '';

            // Format date for datetime-local input
            let startTime = '';
            if (event.start_time) {
                const date = new Date(event.start_time);
                startTime = date.toISOString().slice(0, 16);
            }

            setFormData({
                name_en: nameEn,
                name_tl: nameTl,
                description_en: descEn,
                description_tl: descTl,
                start_time: startTime,
                place_name: event.place?.name || (typeof event.place === 'string' ? event.place : ''),
                place_address: event.place?.address || '',
                mapsUrl: event.place?.mapsUrl || '',
                maxSlots: event.maxSlots || 5
            });
            setError('');
            setShowDeleteConfirm(false);
        }
    }, [event, isOpen]);

    if (!isOpen || !event) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (error) setError('');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.name_en || !formData.start_time || !formData.place_name) {
            setError(t('event_fill_required'));
            return;
        }

        setLoading(true);

        try {
            const token = await getAuthToken();
            const response = await fetch(`/api/events/${event.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    name: {
                        en: formData.name_en.trim(),
                        tl: formData.name_tl.trim() || formData.name_en.trim()
                    },
                    description: {
                        en: formData.description_en.trim(),
                        tl: formData.description_tl.trim() || formData.description_en.trim()
                    },
                    start_time: formData.start_time,
                    place: {
                        name: formData.place_name.trim(),
                        address: formData.place_address.trim() || undefined,
                        mapsUrl: formData.mapsUrl.trim() || undefined,
                        // Preserve existing coordinates if they exist
                        lat: event.place?.lat,
                        lng: event.place?.lng
                    },
                    maxSlots: parseInt(formData.maxSlots, 10)
                })
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to update event');
            }

            if (onSuccess) onSuccess();
            onClose();
        } catch (err) {
            console.error('Error updating event:', err);
            setError(err.message || t('event_edit_failed'));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const token = await getAuthToken();
            const response = await fetch(`/api/events/${event.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to delete event');
            }

            if (onSuccess) onSuccess();
            onClose();
        } catch (err) {
            console.error('Error deleting event:', err);
            setError(err.message || t('event_delete_failed'));
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
                <button className="modal-close-btn" onClick={onClose}>×</button>
                <h3 className="modal-title">{t('event_edit_modal_title')}</h3>
                <p className="modal-subtitle">{t('event_edit_modal_subtitle')}</p>

                {error && <div className="error-banner">{error}</div>}

                {showDeleteConfirm ? (
                    <div className="delete-confirm-container">
                        <div className="delete-confirm-icon">⚠️</div>
                        <h4>{t('event_delete_confirm_title')}</h4>
                        <p>{t('event_delete_confirm_message')}</p>
                        <div className="modal-actions">
                            <button
                                type="button"
                                className="btn-outline-modern"
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={deleting}
                            >
                                {t('cancel')}
                            </button>
                            <button
                                type="button"
                                className="btn-danger-modern"
                                onClick={handleDelete}
                                disabled={deleting}
                            >
                                {deleting ? t('event_deleting') : t('event_delete_confirm_btn')}
                            </button>
                        </div>
                    </div>
                ) : (
                    <form className="modal-form" onSubmit={handleSubmit}>
                        {/* Bilingual Content Section */}
                        <div className="bilingual-section compact">
                            <h4 className="bilingual-section-title">
                                🌐 {t('bilingual_section_title')}
                            </h4>
                            <p className="bilingual-section-help" style={{ fontSize: '0.85rem' }}>{t('bilingual_section_help')}</p>

                            {/* English Content */}
                            <div className="language-block compact">
                                <div className="language-header">
                                    <span className="language-flag">🇺🇸</span>
                                    <span className="language-label">{t('english_content')}</span>
                                </div>

                                <div className="form-group">
                                    <label>{t('create_event_name_en')} *</label>
                                    <input
                                        type="text"
                                        name="name_en"
                                        className="form-input"
                                        value={formData.name_en}
                                        onChange={handleChange}
                                        placeholder={t('create_event_name_en_placeholder')}
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label>{t('create_event_desc_en')}</label>
                                    <textarea
                                        name="description_en"
                                        className="form-input form-textarea"
                                        value={formData.description_en}
                                        onChange={handleChange}
                                        placeholder={t('create_event_desc_en_placeholder')}
                                        rows="2"
                                    />
                                </div>
                            </div>

                            {/* Tagalog Content */}
                            <div className="language-block compact">
                                <div className="language-header">
                                    <span className="language-flag">🇵🇭</span>
                                    <span className="language-label">{t('tagalog_content')}</span>
                                    <span className="optional-badge">{t('optional')}</span>
                                </div>

                                <div className="form-group">
                                    <label>{t('create_event_name_tl')}</label>
                                    <input
                                        type="text"
                                        name="name_tl"
                                        className="form-input"
                                        value={formData.name_tl}
                                        onChange={handleChange}
                                        placeholder={t('create_event_name_tl_placeholder')}
                                    />
                                </div>

                                <div className="form-group">
                                    <label>{t('create_event_desc_tl')}</label>
                                    <textarea
                                        name="description_tl"
                                        className="form-input form-textarea"
                                        value={formData.description_tl}
                                        onChange={handleChange}
                                        placeholder={t('create_event_desc_tl_placeholder')}
                                        rows="2"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Date & Time */}
                        <div className="form-group">
                            <label>{t('create_event_date_time')} *</label>
                            <input
                                type="datetime-local"
                                name="start_time"
                                className="form-input"
                                value={formData.start_time}
                                onChange={handleChange}
                                required
                            />
                        </div>

                        {/* Location */}
                        <div className="form-group">
                            <label>{t('create_event_location_name')} *</label>
                            <input
                                type="text"
                                name="place_name"
                                className="form-input"
                                value={formData.place_name}
                                onChange={handleChange}
                                placeholder={t('create_event_location_placeholder')}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>{t('create_event_address_optional')}</label>
                            <input
                                type="text"
                                name="place_address"
                                className="form-input"
                                value={formData.place_address}
                                onChange={handleChange}
                                placeholder={t('create_event_address_placeholder')}
                            />
                        </div>

                        {/* Google Maps URL */}
                        <div className="form-group">
                            <label>🗺️ {t('create_event_maps_url')}</label>
                            <p className="form-help-text" style={{ fontSize: '0.8rem', marginBottom: '0.5rem' }}>
                                {t('create_event_maps_url_help')}
                            </p>
                            <input
                                type="text"
                                name="mapsUrl"
                                className="form-input"
                                value={formData.mapsUrl}
                                onChange={handleChange}
                                placeholder={t('create_event_maps_url_placeholder')}
                            />
                        </div>

                        {/* Max Slots */}
                        <div className="form-group">
                            <label>{t('create_event_slots')}</label>
                            <input
                                type="number"
                                name="maxSlots"
                                className="form-input"
                                value={formData.maxSlots}
                                onChange={handleChange}
                                min="1"
                                max="1000"
                            />
                        </div>



                        <div className="modal-actions-split">
                            {isAdmin ? (
                                <button
                                    type="button"
                                    className="btn-danger-outline"
                                    onClick={() => setShowDeleteConfirm(true)}
                                    disabled={loading}
                                >
                                    🗑️ {t('delete')}
                                </button>
                            ) : <div></div>}
                            <div className="modal-actions-right">
                                <button
                                    type="button"
                                    className="btn-outline-modern"
                                    onClick={onClose}
                                    disabled={loading}
                                >
                                    {t('cancel')}
                                </button>
                                <button
                                    type="submit"
                                    className="btn-primary-modern"
                                    disabled={loading}
                                >
                                    {loading ? t('event_saving') : t('save_changes')}
                                </button>
                            </div>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
}

export default EditEventModal;
